import {
    Box,
    Dialog,
    Grid,
    IconButton,
    LinearProgress,
    SelectChangeEvent,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getChartA11yOptions } from "../../../../utils/highchartsAccessibility";
import CustomDropdown from "../../../investorOverview/directEquity/directEquityOverview/CustomDropdown";
import {
    useEffect,
    useState
} from "react";
import CircleIcon from '@mui/icons-material/Circle';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import {
    GetHoldingPeriodAnalysisResponseBody, HoldingPeriodSchemeData
} from "../../../../redux-store/types/analytics";
import { HoldingPeriodAnalysisProps } from "./types";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { getHoldingPeriodAnalysis } from "../../../../redux-store/actions/analytics";
import { formatCurrency } from "../../../../utils/utilityFunctions";
import { HoldingPeriodAnalysisLoading } from "../../loadingComponents";
import {
    DialogCloseIcon,
    InfoIconOutlined
} from "../../../../assets/SvgIcons";
import CustomTable from "../../../investorOverview/common/customTable";
import { HoldingPeriodSchemeBody } from "../../tables/tableBodies";
import { HoldingPeriodTableHeader } from "../../tables/tableHeaders";

const dropdownOptions = [
    {
        label: "Equity",
        value: '1'
    },
    {
        label: "Debt",
        value: '2'
    },
    {
        label: "Others",
        value: '3'
    },
]

export default function HoldingPeriodAnalysis({
    clientCode,
    portfolioId
}: HoldingPeriodAnalysisProps): JSX.Element {
    const dispatch = useDispatch();

    const theme = useTheme()

    const tabletScreenSize = useMediaQuery(theme.breakpoints.down(1300))

    const [isLoading, setLoading] = useState<boolean>(false)
    const [data, setData] = useState<GetHoldingPeriodAnalysisResponseBody>({} as GetHoldingPeriodAnalysisResponseBody)
    const [selectedOption, setSelectedOption] = useState<string>("1")
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [tableData, setTableData] = useState<HoldingPeriodSchemeData[]>([])

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const fetchData = async (clientCode: string, portfolioId: string) => {
        try {
            setLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId
            }

            const response = await (dispatch(getHoldingPeriodAnalysis(payload)) as unknown as Promise<GetHoldingPeriodAnalysisResponseBody>);

            setData(response)

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(clientCode, portfolioId)
    }, [clientCode, portfolioId])

    const handleDropdownValueChange = async (event: SelectChangeEvent) => {
        setSelectedOption(event.target.value)
    }

    const shortTermPercentage = data?.result?.labelData?.find(obj => obj.label?.toLowerCase() === "short term")?.value ?? 0
    const longTermPercentage = data?.result?.labelData?.find(obj => obj.label?.toLowerCase() === "long term")?.value ?? 0

    const shortTermValue = data?.result?.data?.find(item => item[0]?.toLowerCase() === "short term")?.[1] ?? 0;
    const longTermValue = data?.result?.data?.find(item => item[0]?.toLowerCase() === "long term")?.[1] ?? 0;

    const graphLegends = data?.result?.labelData?.map(obj => ({
        ...obj,
        circleBgColor: obj?.label?.toLowerCase() === "long term" ? theme.palette.primary[900] : theme.palette.customColors.cyanBlue
    })) ?? []

    const longTermTableData = data?.result?.longTermSchemes ?? [];
    const shortTermTableData = data?.result?.shortTermSchemes ?? [];

    const handleTableModal = (obj: { label: string; value: number }) => {
        if (obj?.label?.toLowerCase() === "long term") {
            setTableData(longTermTableData)
        }
        else {
            setTableData(shortTermTableData)
        }
        setDialogOpen(true)
    }

    if (isLoading) {
        return <HoldingPeriodAnalysisLoading />
    }

    if (!isLoading && !data?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Holding Period Analysis
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        Something went wrong. Please try again.
                    </Typography>
                </Box>
            </Box>

        )
    }

    return (
        <>
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column"
                }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineheight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Holding Period Analysis
                    </Typography>
                    {/* <CustomDropdown
                    isLoading={false}
                    selectedValue={selectedOption}
                    handleValueChange={handleDropdownValueChange}
                    dropdownOptions={dropdownOptions}
                /> */}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: { xs: 'column', lg: 'row' },
                        columnGap: { xs: "0px", lg: "25px" },
                        rowGap: { xs: "35px", lg: "0px" },
                        marginTop: "35px",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                            rowGap: "20px"
                        }}
                    >
                        <Box>
                            {(() => {
                                const holdingPeriodA11y = getChartA11yOptions({
                                    altText: 'Holding period distribution donut chart',
                                    description: `Donut chart comparing long-term holdings (${longTermPercentage}%) and short-term holdings (${shortTermPercentage}%) for the selected asset class.`,
                                });
                                return (
                                    <HighchartsReact
                                        showLoading={true}
                                        highcharts={Highcharts}
                                        options={{
                                            chart: {
                                                type: 'pie',
                                                width: null,
                                                height: "100%",
                                                description: holdingPeriodA11y.chartDescription,
                                            },
                                            title: holdingPeriodA11y.title,
                                            accessibility: holdingPeriodA11y.accessibility,
                                            tooltip: {
                                                enabled: false
                                            },
                                            plotOptions: {
                                                series: {
                                                    colorByPoint: true,
                                                    size: '100%',
                                                    innerSize: '85%',
                                                    dataLabels: {
                                                        enabled: true,
                                                        connectorWidth: 0
                                                    }
                                                }
                                            },
                                            credits: {
                                                enabled: false
                                            },
                                            colors: [theme.palette.primary[900], theme.palette.customColors.cyanBlue],
                                            series: [{
                                                name: "",
                                                data: [
                                                    ["", longTermPercentage],
                                                    ["", shortTermPercentage]
                                                ]
                                            }]
                                        }}
                                        allowChartUpdate={true}
                                    />
                                );
                            })()}
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                flexDirection: 'column',
                                rowGap: "10px"
                            }}
                        >
                            {graphLegends.map(obj => (
                                <Grid
                                    key={obj.label}
                                    container
                                    columnSpacing={2}
                                    sx={{ justifyContent: "center" }}
                                >
                                    <Grid
                                        item
                                        xs={2}
                                        sx={{ textAlign: "center" }}
                                    >
                                        <CircleIcon
                                            style={{
                                                color: obj.circleBgColor,
                                                width: '11.69px',
                                                height: '11.69px',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="subtitle_400"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "21.25px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            {obj?.label ?? ""}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography
                                                variant="subtitle_400"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "21.25px",
                                                    color: theme.palette.text.primary
                                                })}
                                            >
                                                {obj?.value ?? 0}%
                                            </Typography>
                                            <IconButton onClick={() => handleTableModal(obj)}>
                                                <InfoIconOutlined />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1.2 }}>
                        <Box
                            sx={(theme: Theme) => ({
                                paddingBottom: '20px',
                                borderBottom: `0.5px solid ${theme.palette.customColors.pastelGrey}`
                            })}
                        >
                            <Typography
                                variant="subtitle_400"
                                sx={(theme: Theme) => ({
                                    lineHeight: "21.25px",
                                    color: theme.palette.common.black,
                                })}
                            >
                                Short Term Capital Gains
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: "18px"
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                        rowGap: "10px"
                                    }}
                                >
                                    <Typography
                                        variant="overline_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "17.5px",
                                            color: theme.palette.grey[500]
                                        })}
                                    >
                                        Gains
                                    </Typography>
                                    <Typography
                                        variant="overline_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "17.5px",
                                            color: theme.palette.text.primary,
                                            fontWeight: 600
                                        })}
                                    >
                                        {formatCurrency({ value: shortTermValue, locale, currencyCode })}
                                    </Typography>
                                </Box>
                                {/* <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "flex-end",
                                    rowGap: "10px"
                                }}
                            >
                                <Typography
                                    variant="overline_400"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "17.5px",
                                        color: theme.palette.grey[500]
                                    })}
                                >
                                    Tax
                                </Typography>
                                <Typography
                                    variant="overline_400"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "17.5px",
                                        color: theme.palette.text.primary,
                                        fontWeight: 600
                                    })}
                                >
                                    ₹ 7,750
                                </Typography>
                            </Box> */}
                            </Box>
                        </Box>
                        <Box sx={{ paddingTop: '20px', }}>
                            <Typography
                                variant="subtitle_400"
                                sx={(theme: Theme) => ({
                                    lineHeight: "21.25px",
                                    color: theme.palette.common.black,
                                })}
                            >
                                Long Term Capital Gains
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: "18px"
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                        rowGap: "10px"
                                    }}
                                >
                                    <Typography
                                        variant="overline_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "17.5px",
                                            color: theme.palette.grey[500]
                                        })}
                                    >
                                        Gains
                                    </Typography>
                                    <Typography
                                        variant="overline_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "17.5px",
                                            color: theme.palette.text.primary,
                                            fontWeight: 600
                                        })}
                                    >
                                        {formatCurrency({ value: longTermValue, locale, currencyCode })}
                                    </Typography>
                                </Box>
                                {/* <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "flex-end",
                                    rowGap: "10px"
                                }}
                            >
                                <Typography
                                    variant="overline_400"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "17.5px",
                                        color: theme.palette.grey[500]
                                    })}
                                >
                                    Tax
                                </Typography>
                                <Typography
                                    variant="overline_400"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "17.5px",
                                        color: theme.palette.text.primary,
                                        fontWeight: 600
                                    })}
                                >
                                    ₹ 1,34,375
                                </Typography>
                            </Box> */}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {/* <Box sx={{ marginTop: "70px" }}>
                <Typography
                    sx={(theme: Theme) => ({
                        fontSize: "14.65px",
                        fontWeight: 500,
                        lineHeight: "18px",
                        color: theme.palette.common.black
                    })}
                >
                    Tax Exemption Under 80C
                </Typography>
                <Box sx={{ marginTop: "12px" }}>
                    <Typography
                        sx={(theme: Theme) => ({
                            fontSize: "10.65px",
                            fontWeight: 500,
                            lineHeight: "13px",
                            color: theme.palette.grey[700],
                            marginBottom: "8px"
                        })}
                    >
                        Limit
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={47}
                        sx={(theme: Theme) => ({
                            background: '#D9D9D9',
                            [`&.${linearProgressClasses.colorPrimary}`]: {
                                background: 'D9D9D9'
                            },
                            [`& .${linearProgressClasses.bar}`]: {
                                background: theme.palette.success[800]
                            }
                        }
                        )}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "8px"
                        }}
                    >
                        <Typography
                            sx={(theme: Theme) => ({
                                fontSize: "10.65px",
                                fontWeight: 500,
                                lineHeight: "13px",
                                color: theme.palette.grey[700],
                                marginBottom: "8px"
                            })}
                        >
                            0
                        </Typography>
                        <Typography
                            sx={(theme: Theme) => ({
                                fontSize: "10.65px",
                                fontWeight: 500,
                                lineHeight: "13px",
                                color: theme.palette.grey[700],
                                marginBottom: "8px"
                            })}
                        >
                            1,50,000
                        </Typography>
                    </Box>
                    <Box sx={{ marginTop: "13px" }}>
                        <Typography
                            sx={(theme: Theme) => ({
                                fontSize: "10.65px",
                                fontWeight: 500,
                                lineHeight: "13px",
                                color: theme.palette.common.black
                            })}
                        >
                            Invested Amount : 89,000
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "10.65px",
                                fontWeight: 500,
                                lineHeight: "13px",
                                color: "#504E4E",
                                marginTop: '7px'
                            }}
                        >
                            The invested amount in ELSS will grant TAX exemption benefits under 80 C for the financial year ‘24 to ‘25
                        </Typography>
                    </Box>

                </Box>
            </Box> */}
            </Box>
            <Dialog
                onClose={() => setDialogOpen(false)}
                open={dialogOpen}
                maxWidth='lg'
                fullWidth
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '25px',
                        backgroundColor: theme.palette.common.white,
                        padding: "35px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        height: "80%"
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={(theme: Theme) => ({
                            lineHeight: "32.5px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Schemes
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            columnGap: "20px"
                        }}
                    >
                        <IconButton
                            aria-label="dialog-close"
                            onClick={() => setDialogOpen(false)}
                            sx={{ padding: "5px" }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Box
                    sx={{
                        marginTop: "35px",
                        maxHeight: "538px",
                        overflowY: "auto"
                    }}
                >
                    <CustomTable
                        isLoading={false}
                        tableBody={
                            <HoldingPeriodSchemeBody
                                tableBodyData={tableData || []}
                            />
                        }
                        tableData={tableData || []}
                        tableHeaderData={HoldingPeriodTableHeader}
                        tableHeadRowCustomStyles={{
                            '& th:first-of-type': {
                                paddingLeft: "0px"
                            }
                        }}

                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        loadingRowsNum={7}
                        tableDataCount={tableData.length}
                        isPaginationVisible={false}
                    />
                </Box>
            </Dialog >
        </>
    )
}